// Lib
import React from 'react';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Components
import ListButton from '../../../Button/ListButton';
import GridButton from '../../../Button/GridButton';
import ListCard from '../../../Products/Card/ListCard';
import OrderProductDetails from '../../Modals/OrderProductDetails/OrderProductDetails';
import OrderProductCardContent from '../../Cards/OrderProductCardContent';
import GridCard from '../../../Products/Card/GridCard';

// Hooks
import useUI from '../../../../hooks/useUI';
import { useTheme } from '@mui/material/styles';
import PlusButton from '../../Buttons/PlusButton';
import CloseComponent from '../../Cards/OrderProductCardContent/CloseComponent';

// Styles
import { useStyles } from './OrderedProductsStyles';

const LIST_CARD_IMAGE_SIZE_DESKTOP = 230;
const LIST_CARD_IMAGE_SIZE_TABLET_MIN = 245;
const LIST_CARD_IMAGE_SIZE_TABLET_MAX = 263;
const LIST_CARD_IMAGE_SIZE_MOBILE = 136;
const GRID_CARD_IMAGE_SIZE_DESKTOP = 250;
const GRID_CARD_IMAGE_SIZE_TABLET = 200;
const GRID_CARD_IMAGE_SIZE_MOBILE = 138;

const OrderedProducts = ({
  orderProducts,
  setListView = () => {},
  listView = true,
  orderDetails,
  isRecurring = false,
  addressesCount = 1,
}) => {
  const navigate = useNavigate();
  const { isTabletMode, isMobileMode } = useUI();
  const { classes } = useStyles({ listView, isMobileMode, isRecurring });
  const theme = useTheme();

  const getOrderItem = (productId) => {
    return orderDetails.order_items.find((item) => item.product === productId);
  };

  return (
    <Box mb={4}>
      <Box
        className={classes.productsHeader}
        display={'flex'}
        justifyContent={'space-between'}>
        <Typography fontSize={{ lg: 28, md: 18, xs: 16 }} variant="h2" mb={2}>
          Products{' '}
          {isRecurring && (
            <PlusButton
              onClick={() =>
                navigate(
                  `/order-history/recurring/${orderDetails.id}/materials`,
                )
              }
              className={classes.plusButton}
            />
          )}
        </Typography>
        <Box display={'flex'} gap={2}>
          <ListButton
            isActive={listView}
            onClick={() => setListView(true)}></ListButton>
          <GridButton
            isActive={!listView}
            onClick={() => setListView(false)}></GridButton>
        </Box>
      </Box>
      {orderProducts?.ids?.length > 0 && (
        <Box
          data-testid={`ordered-products-${listView ? 'list' : 'grid'}`}
          className={classes.productsLayout}>
          {orderProducts?.ids?.map((productId) => {
            const product = orderProducts.products?.[productId];

            return listView ? (
              <ListCard
                key={productId}
                isTabletMode={isTabletMode}
                product={product}
                cardModalComponent={OrderProductDetails}
                modalProps={{
                  addressesCount: addressesCount,
                  order: orderDetails,
                  orderItem: getOrderItem(productId),
                  isRecurring,
                }}
                cardContent={
                  <OrderProductCardContent
                    addressesCount={addressesCount}
                    isRecurring={isRecurring}
                    product={product}
                    order={orderDetails}
                    listView
                    orderItem={getOrderItem(productId)}
                  />
                }
                imageContainer={{
                  desktop: [LIST_CARD_IMAGE_SIZE_DESKTOP],
                  tablet: [
                    LIST_CARD_IMAGE_SIZE_TABLET_MIN,
                    LIST_CARD_IMAGE_SIZE_TABLET_MAX,
                  ],
                  mobile: [
                    LIST_CARD_IMAGE_SIZE_MOBILE,
                    LIST_CARD_IMAGE_SIZE_MOBILE,
                  ],
                }}
                customCardSize={{
                  height: 230,
                  [theme.breakpoints.down('lg')]: {
                    height: 263,
                  },
                }}
              />
            ) : (
              <GridCard
                CloseComponent={() =>
                  isRecurring && (
                    <CloseComponent
                      orderItem={getOrderItem(productId)}
                      product={product}
                      order={orderDetails}
                    />
                  )
                }
                className={classes.gridCard}
                key={productId}
                isTabletMode={isTabletMode}
                product={product}
                cardModalComponent={OrderProductDetails}
                modalProps={{
                  addressesCount: addressesCount,
                  order: orderDetails,
                  orderItem: getOrderItem(productId),
                  isRecurring,
                }}
                cardContent={
                  <OrderProductCardContent
                    addressesCount={addressesCount}
                    isRecurring={isRecurring}
                    product={product}
                    order={orderDetails}
                    orderItem={getOrderItem(productId)}
                  />
                }
                imageContainer={{
                  desktop: ['100%', GRID_CARD_IMAGE_SIZE_DESKTOP],
                  tablet: ['100%', GRID_CARD_IMAGE_SIZE_TABLET],
                  mobile: ['100%', GRID_CARD_IMAGE_SIZE_MOBILE],
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default OrderedProducts;
